import { useBreakpointMinLg } from '@kk/shared/hooks/useBreakpoint';
import type { PressEvent } from '@react-types/shared';
import { cn } from 'cn';
import type React from 'react';
import type { ButtonHTMLAttributes } from 'react';
import type { NotificationType } from '#ui/constants';
import { useTheme } from '#ui/hooks/useTheme.js';
import type { IconProps, IconType } from '../Icon';
import type { ButtonTheme as Theme } from './index';
import { ButtonContent } from './private/ButtonContent';
import { ButtonSpinner } from './private/ButtonSpinner';
import { type ButtonVariantProps, buttonVariants } from './styles';

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'>,
    ButtonVariantProps {
  theme?: Theme;
  disabled?: boolean | undefined;
  children?: React.ReactNode;
  label?: string;
  icon?: IconType;
  iconProps?: Omit<IconProps, 'type'>;
  onPress?: (e: React.MouseEvent<HTMLButtonElement> | PressEvent) => void;
}

export const mapNotificationType = (type: NotificationType): Theme => {
  switch (type) {
    case 'success':
      return 'subtle';
    case 'default':
    case 'info':
      return 'primary';
    case 'danger':
      return 'danger';
    default:
      return type as Theme;
  }
};

function getCompactValue(
  compact: boolean | null | undefined,
  theme: string | null | undefined,
  minLg: boolean,
): boolean | undefined {
  return compact ?? (theme === 'kunde' ? minLg : undefined);
}

function handleButtonClick(
  event: React.MouseEvent<HTMLButtonElement>,
  onPress: ButtonProps['onPress'],
  onClick: ButtonProps['onClick'],
): void {
  onPress?.(event);
  onClick?.(event);
}

export function Button(props: ButtonProps) {
  const currentTheme = useTheme();
  const minLg = useBreakpointMinLg();

  const {
    theme = 'default',
    icon,
    compact: compactProp,
    inverted = false,
    rightAligned,
    label,
    children,
    loading,
    disabled,
    className,
    onPress,
    onClick,
    iconProps,
    hasIcon,
    hasLabel,
    ...rest
  } = props;
  const compact = getCompactValue(compactProp, currentTheme, minLg);
  const resolvedHasIcon = hasIcon ?? !!icon;
  const resolvedHasLabel = hasLabel ?? !!(label || children);

  return (
    <button
      className={cn(
        buttonVariants({
          theme,
          compact,
          rightAligned,
          loading,
          inverted,
          hasIcon: resolvedHasIcon,
          hasLabel: resolvedHasLabel,
        }),
        className,
      )}
      disabled={!!disabled}
      {...rest}
      onClick={(event) => handleButtonClick(event, onPress, onClick)}
    >
      {loading ? (
        <ButtonSpinner compact={compact} isLaane={currentTheme === 'laane'} />
      ) : (
        <ButtonContent
          compact={compact}
          icon={icon}
          iconProps={iconProps}
          label={label}
          rightAligned={rightAligned}
        >
          {children}
        </ButtonContent>
      )}
    </button>
  );
}

export default Button;
